import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  CheckCircle,
  Clock,
  FilePenLine,
  AlertCircle,
  XCircle,
  Package,
  CreditCard,
  LogOut,
  FileText,
  Truck,
  Lock,
  ArrowRight,
  Shield,
  Leaf,
  Award,
  RefreshCw,
  Calendar,
  User,
  Mail,
  Phone,
  DollarSign,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import PaymentModal from "../../Payment/PaymentModal";
import PaymentMethodModal from "../../Payment/PaymentMethodModal";

const ClientDashboard = () => {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isPaymentMethodModalOpen, setIsPaymentMethodModalOpen] = useState(false);
  const [total, setTotal] = useState(0); // State pour le total
  const [isVisible, setIsVisible] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const { id } = useParams();
  const [sizesData, setSizesData] = useState([]);

  // Fonction pour récupérer les données des sizes depuis l'API
  const fetchSizesData = useCallback(async () => {
    try {
      const response = await axios.get(
        'https://api.greenecobagsusa.com/api/sizes',
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`
          }
        }
      );

      if (response.data) {
        setSizesData(response.data);
        console.log('Sizes data fetched:', response.data);
      }
    } catch (err) {
      console.error('Error fetching sizes data:', err);
    }
  }, []);

  const getSizePrice = (sizeName) => {
    const sizeData = sizesData.find(
      size => size.name.toLowerCase() === sizeName.toLowerCase() ||
        size.label.toLowerCase() === sizeName.toLowerCase()
    );
    return sizeData ? parseFloat(sizeData.price) : 0;
  };


  const navigate = useNavigate();

  // Refresh interval in ms (every 30 seconds)
  const REFRESH_INTERVAL = 30000;

  // Function to fetch total amount from API
  const fetchQuoteTotal = useCallback(async () => {
    if (!id) {
      console.error("No quote ID available");
      return;
    }

    try {
      const response = await axios.get(
        `https://api.greenecobagsusa.com/api/quotes/${id}/total`,
        // https://api.greenecobagsusa.com/api/quotes/%7Bid%7D/total
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`
          }
        }
      );

      if (response.data && response.data.total_quote !== undefined) {
        const totalAmount = parseFloat(response.data.total_quote) || 0;
        setTotal(totalAmount);
        console.log(`Fetched total for quote #${id}:`, totalAmount);
      } else {
        console.error("Quote not found or invalid response");
        setTotal(0);
      }
    } catch (err) {
      console.error(`Error fetching quote total for ID ${id}:`, err);
      if (err.response && err.response.status === 404) {
        console.error("This quote does not exist");
        setError("This quote does not exist");
      }
      setTotal(0);
    }
  }, [id]);

  // Helper function to safely parse sizes_quantities
  const parseSizesQuantities = (sizesData) => {
    if (!sizesData) return {};

    if (typeof sizesData === 'object' && !Array.isArray(sizesData)) {
      return sizesData;
    }

    if (typeof sizesData === 'string') {
      try {
        const parsed = JSON.parse(sizesData);
        if (typeof parsed === 'object' && !Array.isArray(parsed)) {
          return parsed;
        }
      } catch (e) {
        console.error('Error parsing sizes_quantities string:', e);
      }
    }

    console.warn('sizes_quantities is not in expected format:', sizesData);
    return {};
  };

  // Function to check authorized access
  const checkAuthorizedAccess = useCallback((storedQuote, requestedId) => {
    if (storedQuote && storedQuote.id && storedQuote.id.toString() !== requestedId.toString()) {
      navigate(`/client/dashboard/${storedQuote.id}`);
      return false;
    }
    return true;
  }, [navigate]);

  // Function to fetch quote data from the API
  const fetchQuoteStatus = useCallback(async () => {
    try {
      const storedQuoteData = localStorage.getItem("clientQuote");
      const storedQuote = storedQuoteData ? JSON.parse(storedQuoteData) : null;

      if (!storedQuote || !checkAuthorizedAccess(storedQuote, id)) {
        return;
      }

      const response = await axios.get(
        `https://api.greenecobagsusa.com/api/quotes/${storedQuote.id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("clientToken")}`
          }
        }
      );

      if (response.data && response.data.quote) {
        const quoteData = response.data.quote;

        if (storedQuote.email !== quoteData.email) {
          throw new Error("Unauthorized access");
        }

        console.log("API Response - Quote Data:", quoteData);

        const updatedQuote = {
          id: quoteData.id,
          name: quoteData.name,
          email: quoteData.email,
          phone: quoteData.phone,
          quantity: quoteData.quantity,
          status: quoteData.status,
          rejection_reason: quoteData.rejection_reason || null,
          created_at: quoteData.created_at,
          sizes: quoteData.sizes || [],
          sizes_quantities: quoteData.sizes_quantities || {},
          is_logo_required: quoteData.is_logo_required,
          price_logo_quote: quoteData.price_logo_quote,
        };

        console.log("Updated Quote with logo data:", updatedQuote);

        setQuote(updatedQuote);
        localStorage.setItem("clientQuote", JSON.stringify(updatedQuote));

        // Fetch the total amount
        await fetchQuoteTotal();

        if (loading) {
          setLoading(false);
        }
      }
    } catch (err) {
      console.error("Error fetching status:", err);
      if (err.message === "Unauthorized access") {
        setError("You are not authorized to access this quote");
        navigate("/client/login");
      }
    }
  }, [checkAuthorizedAccess, id, loading, navigate, fetchQuoteTotal]);

  // Get quote data from localStorage on initial load
  useEffect(() => {
    const storedQuote = localStorage.getItem("clientQuote");

    if (storedQuote) {
      try {
        const parsedQuote = JSON.parse(storedQuote);

        if (!checkAuthorizedAccess(parsedQuote, id)) {
          return;
        }

        setQuote(parsedQuote);
        setLoading(false);
        setIsVisible(true);

        // Fetch updated status, total, and sizes data
        fetchQuoteStatus();
        fetchQuoteTotal();
        fetchSizesData(); // Ajouter ceci

      } catch (err) {
        console.error("Error parsing data:", err);
        setError("Unable to load your data");
        setLoading(false);
      }
    } else {
      navigate("/client/login");
    }

    // Set up refresh interval
    const intervalId = setInterval(() => {
      fetchQuoteStatus();
      fetchQuoteTotal();
      fetchSizesData(); // Ajouter ceci
    }, REFRESH_INTERVAL);

    return () => clearInterval(intervalId);
  }, [checkAuthorizedAccess, fetchQuoteStatus, fetchQuoteTotal, fetchSizesData, navigate, id]);

  const handleLogout = () => {
    localStorage.removeItem("clientQuote");
    localStorage.removeItem("clientToken");
    navigate("/");
  };

  const handlePaymentMethodClick = () => {
    setIsPaymentMethodModalOpen(true);
  };

  const handlePaymentModalOpen = () => {
    setIsPaymentMethodModalOpen(false);
    setIsPaymentModalOpen(true);
  };

  const handlePaypalPayment = () => {
    try {
      setIsPaymentMethodModalOpen(false);

      if (!quote || !quote.id) {
        console.error("Invalid or missing quote");
        return;
      }

      if (!total || total <= 0) {
        console.error("Invalid quote amount");
        return;
      }

      const clientToken = localStorage.getItem("clientToken");
      if (!clientToken) {
        console.error("Missing authentication token");
        return;
      }

      console.log("Preparing PayPal payment for:", {
        quote_id: quote.id,
        amount: total
      });

      console.log("PayPal redirect to be implemented");
    } catch (error) {
      console.error("Error initializing PayPal payment:", error);
    }
  };

  const handlePaymentSuccess = (updatedQuote) => {
    localStorage.setItem("clientQuote", JSON.stringify(updatedQuote));
    setQuote(updatedQuote);
    setIsPaymentModalOpen(false);
    // Refresh total after payment
    fetchQuoteTotal();
  };

  const getStatusDisplay = (status) => {
    switch (status) {
      case "pending":
        return {
          icon: <Clock className="text-yellow-500" size={24} />,
          text: "Pending",
          color: "text-yellow-500",
          bg: "bg-yellow-50",
        };
      case "approved":
        return {
          icon: <CheckCircle className="text-green-500" size={24} />,
          text: "Approved",
          color: "text-green-500",
          bg: "bg-green-50",
        };
      case "confirmed":
        return {
          icon: <Package className="text-blue-500" size={24} />,
          text: "Confirmed",
          color: "text-blue-500",
          bg: "bg-blue-50",
        };
      case "rejected":
        return {
          icon: <XCircle className="text-red-500" size={24} />,
          text: "Rejected",
          color: "text-red-500",
          bg: "bg-red-50",
        };
      case "paid":
        return {
          icon: <CheckCircle className="text-green-500" size={24} />,
          text: "Paid",
          color: "text-green-500",
          bg: "bg-green-50",
        };
      default:
        return {
          icon: <AlertCircle className="text-gray-500" size={24} />,
          text: "Undetermined",
          color: "text-gray-500",
          bg: "bg-gray-50",
        };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin h-16 w-16 rounded-full border-4 border-green-200 border-t-green-600 mx-auto"></div>
            <div className="absolute inset-0 animate-ping h-16 w-16 rounded-full border-4 border-green-300 opacity-20"></div>
          </div>
          <div className="mt-6 space-y-2">
            <h3 className="text-xl font-semibold text-gray-700">Loading Your Dashboard</h3>
            <p className="text-gray-500">Please wait while we fetch your quote information...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-pink-50 to-rose-50 flex flex-col items-center justify-center p-4">
        <div className="max-w-md mx-auto text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Oops! Something went wrong</h1>
          <p className="text-gray-600 mb-8">{error}</p>
          <button
            onClick={() => navigate("/client/login")}
            className="bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 text-white px-8 py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-amber-50 to-orange-50 flex flex-col items-center justify-center p-4">
        <div className="max-w-md mx-auto text-center">
          <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="w-10 h-10 text-yellow-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-4">No Quote Found</h1>
          <p className="text-gray-600 mb-8">We couldn't find your quote information. Please try logging in again.</p>
          <button
            onClick={() => navigate("/client/login")}
            className="bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 text-white px-8 py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  const statusDisplay = getStatusDisplay(quote.status);

  // Date formatting
  const formattedDate = quote.created_at
    ? new Date(quote.created_at).toLocaleDateString()
    : "Date not available";

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-20 left-10 w-32 h-32 bg-green-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-emerald-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-24 h-24 bg-teal-200 rounded-full mix-blend-multiply filter blur-xl opacity-15 animate-pulse animation-delay-4000"></div>
      </div>

      {/* Stripe Payment Modal */}
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        quote={quote}
        onPaymentSuccess={handlePaymentSuccess}
        totalAmount={total}
      />

      {/* Payment Method Choice Modal */}
      <PaymentMethodModal
        isOpen={isPaymentMethodModalOpen}
        onClose={() => setIsPaymentMethodModalOpen(false)}
        onStripePayment={handlePaymentModalOpen}
        onPaypalPayment={handlePaypalPayment}
        quote={quote}
        totalAmount={total}
      />

      {/* Modern Header */}
      <header className="bg-white/80 backdrop-blur-lg shadow-lg border-b border-white/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl flex items-center justify-center">
                <Package className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Order Tracking</h1>
                <p className="text-sm text-gray-600">Monitor your quote progress</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => {
                  setRefreshing(true);
                  Promise.all([fetchQuoteStatus(), fetchQuoteTotal()])
                    .finally(() => setRefreshing(false));
                }}
                className="flex items-center space-x-2 px-4 py-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg transition-colors duration-300"
                disabled={refreshing}
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors duration-300"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Status Card */}
        <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-8">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-4 lg:space-y-0">
              <div className="flex items-start space-x-4">
                <div className={`p-3 rounded-xl ${statusDisplay.bg}`}>
                  {statusDisplay.icon}
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Order Status:{" "}
                    <span className={statusDisplay.color}>
                      {statusDisplay.text}
                    </span>
                  </h3>
                  <p className="text-gray-600 text-lg">
                    {quote.status === "pending" &&
                      "Your quote is being reviewed by our team. We'll notify you once it's processed."}
                    {quote.status === "approved" &&
                      "Great news! Your quote has been approved. You can now proceed to secure payment."}
                    {quote.status === "confirmed" &&
                      "Your order is confirmed and being processed. We'll keep you updated on the progress."}
                    {quote.status === "rejected" &&
                      `Unfortunately, your quote has been rejected. Reason: ${quote.rejection_reason || "Not specified"}`}
                    {quote.status === "paid" &&
                      "Payment received! Your order is being prepared for delivery."}
                  </p>
                </div>
              </div>

              {/* Payment button */}
              {(quote.status === "approved" || quote.status === "confirmed") && (
                <button
                  onClick={handlePaymentMethodClick}
                  className="group relative bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-xl overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-all duration-700"></div>
                  <div className="flex items-center space-x-3 relative z-10">
                    <CreditCard className="w-5 h-5" />
                    <span>Pay ${total.toFixed(2)}</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                  </div>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Quote Details Card */}
        <div className={`transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 overflow-hidden">
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-8 py-6">
              <h3 className="text-2xl font-bold text-white flex items-center">
                <FileText className="mr-3" size={24} />
                Quote Details
              </h3>
              <p className="text-green-100 mt-1">Complete information about your order</p>
            </div>

            <div className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {/* Reference */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Shield className="w-4 h-4" />
                    <span className="text-sm font-medium">Reference</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    #{quote.id || "N/A"}
                  </p>
                </div>

                {/* Creation Date */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Calendar className="w-4 h-4" />
                    <span className="text-sm font-medium">Creation Date</span>
                  </div>
                  <p className="text-lg font-semibold text-gray-900">{formattedDate}</p>
                </div>

                {/* Total Amount */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 text-gray-600">
                    <DollarSign className="w-4 h-4" />
                    <span className="text-sm font-medium">Total Amount</span>
                  </div>
                  <p className="text-3xl font-bold text-green-600">
                    ${total.toFixed(2)}
                  </p>
                </div>

                {/* Customer Name */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 text-gray-600">
                    <User className="w-4 h-4" />
                    <span className="text-sm font-medium">Customer Name</span>
                  </div>
                  <p className="text-lg font-semibold text-gray-900">
                    {quote.name || "N/A"}
                  </p>
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Mail className="w-4 h-4" />
                    <span className="text-sm font-medium">Email</span>
                  </div>
                  <p className="text-lg font-semibold text-gray-900">
                    {quote.email || "N/A"}
                  </p>
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Phone className="w-4 h-4" />
                    <span className="text-sm font-medium">Phone</span>
                  </div>
                  <p className="text-lg font-semibold text-gray-900">
                    {quote.phone || "N/A"}
                  </p>
                </div>
              </div>

              {/* Quantity Details Section */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <h4 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                  <Package className="w-5 h-5 mr-2 text-green-600" />
                  Quantity Details
                </h4>

                <div className="space-y-4">
                  {(() => {
                    const parsedSizes = parseSizesQuantities(quote.sizes_quantities);
                    return Object.keys(parsedSizes).length > 0 ? (
                      <div className="space-y-3">
                        {Object.entries(parsedSizes)
                          .filter(([, qty]) => parseInt(qty) > 0)
                          .map(([sizeName, qty]) => {
                            const quantity = parseInt(qty) || 0;
                            const pricePerUnit = getSizePrice(sizeName);
                            const subtotal = quantity * pricePerUnit;

                            return (
                              <div key={sizeName} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                                <div className="flex justify-between items-center">
                                  <div className="flex-1">
                                    <h5 className="font-semibold text-gray-900 text-lg">{sizeName}</h5>
                                    <div className="flex items-center space-x-2 text-gray-600 mt-1">
                                      <span>{quantity.toLocaleString()} units</span>
                                      <span className="text-gray-400">×</span>
                                      <span>${pricePerUnit.toFixed(2)}/unit</span>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <div className="text-2xl font-bold text-gray-900">
                                      ${subtotal.toFixed(3)}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                      Subtotal
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}

                        {/* Logo printing info */}
                        {quote.is_logo_required === true &&
                          quote.price_logo_quote &&
                          !isNaN(parseFloat(quote.price_logo_quote)) &&
                          parseFloat(quote.price_logo_quote) > 0 && (
                            <div className="bg-green-50 rounded-xl p-4 border border-green-200 mt-2">
                              <div className="flex justify-between items-center">
                                <div className="flex-1">
                                  <h5 className="font-semibold text-gray-900 text-lg">Logo Printing</h5>
                                  <p className="text-gray-600">One-time setup fee</p>
                                </div>
                                <div className="text-right">
                                  <div className="text-2xl font-bold text-green-700">
                                    ${parseFloat(quote.price_logo_quote).toFixed(2)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                        {/* Total Summary */}
                        <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl p-4 border border-green-300 mt-4">
                          <div className="flex justify-between items-center">
                            <div className="flex-1">
                              <h5 className="font-bold text-white text-xl">Total Amount</h5>
                              <p className="text-green-100">Including all items and fees</p>
                            </div>
                            <div className="text-right">
                              <div className="text-3xl font-bold text-white">
                                ${total.toFixed(2)}
                              </div>
                            </div>
                          </div>
                        </div>

                      </div>
                    ) : (
                      <div className="bg-yellow-50 rounded-xl p-6 border border-yellow-200 text-center">
                        <AlertTriangle className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                        <p className="text-gray-600">
                          {quote.quantity ? `${quote.quantity.toLocaleString()} units` : 'No quantity information available'}
                        </p>
                        {quote.sizes && <p className="text-sm text-gray-500 mt-1">({quote.sizes})</p>}
                        <p className="text-xs text-yellow-600 mt-2">
                          Note: Detailed size breakdown not available
                        </p>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Tracker */}
        <div className={`transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6">
              <h3 className="text-2xl font-bold text-white flex items-center">
                <Truck className="mr-3" size={24} />
                Order Progress
              </h3>
              <p className="text-blue-100 mt-1">Track your order through each stage</p>
            </div>

            <div className="p-8">
              <div className="relative">
                {/* Progress Line */}
                <div className="absolute top-6 left-0 right-0 h-1 bg-gray-200 rounded-full">
                  <div
                    className={`h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full transition-all duration-1000 ${quote.status === "paid" ? "w-full" :
                      quote.status === "confirmed" ? "w-3/4" :
                        quote.status === "approved" ? "w-1/2" :
                          "w-1/4"
                      }`}
                  ></div>
                </div>

                {/* Progress Steps */}
                <div className="relative flex justify-between">
                  {/* Step 1: Quote Submitted */}
                  <div className="flex flex-col items-center">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center border-4 transition-all duration-500 ${quote.status ?
                      "bg-green-500 border-green-500 text-white shadow-lg" :
                      "bg-white border-gray-300 text-gray-400"
                      }`}>
                      <FilePenLine size={20} />
                    </div>
                    <div className="mt-4 text-center">
                      <p className="font-semibold text-gray-900">Quote Submitted</p>
                      <p className="text-sm text-gray-600">Your request is received</p>
                    </div>
                  </div>

                  {/* Step 2: Quote Approved */}
                  <div className="flex flex-col items-center">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center border-4 transition-all duration-500 ${quote.status === "approved" || quote.status === "confirmed" || quote.status === "paid" ?
                      "bg-green-500 border-green-500 text-white shadow-lg" :
                      "bg-white border-gray-300 text-gray-400"
                      }`}>
                      <CheckCircle size={20} />
                    </div>
                    <div className="mt-4 text-center">
                      <p className="font-semibold text-gray-900">Quote Approved</p>
                      <p className="text-sm text-gray-600">Ready for payment</p>
                    </div>
                  </div>

                  {/* Step 3: Payment Confirmed */}
                  <div className="flex flex-col items-center">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center border-4 transition-all duration-500 ${quote.status === "confirmed" || quote.status === "paid" ?
                      "bg-green-500 border-green-500 text-white shadow-lg" :
                      "bg-white border-gray-300 text-gray-400"
                      }`}>
                      <CreditCard size={20} />
                    </div>
                    <div className="mt-4 text-center">
                      <p className="font-semibold text-gray-900">Payment Confirmed</p>
                      <p className="text-sm text-gray-600">Order processing</p>
                    </div>
                  </div>

                  {/* Step 4: Delivery */}
                  <div className="flex flex-col items-center">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center border-4 transition-all duration-500 ${quote.status === "paid" ?
                      "bg-green-500 border-green-500 text-white shadow-lg" :
                      "bg-white border-gray-300 text-gray-400"
                      }`}>
                      <Package size={20} />
                    </div>
                    <div className="mt-4 text-center">
                      <p className="font-semibold text-gray-900">Delivery</p>
                      <p className="text-sm text-gray-600">On its way to you</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        {(quote.status === "approved" || quote.status === "confirmed") && (
          <div className={`transition-all duration-1000 delay-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 overflow-hidden">
              <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-8 py-6">
                <h3 className="text-2xl font-bold text-white flex items-center">
                  <Lock className="mr-3" size={24} />
                  Finalize Your Order
                </h3>
                <p className="text-green-100 mt-1">Complete your payment to proceed</p>
              </div>

              <div className="p-8">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center space-x-2 bg-green-50 text-green-700 px-4 py-2 rounded-full text-sm font-medium">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Ready for Payment</span>
                  </div>
                </div>

                <button
                  onClick={handlePaymentMethodClick}
                  className="group relative w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-8 py-6 rounded-xl font-bold text-xl transition-all duration-300 transform hover:scale-105 hover:shadow-2xl overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-all duration-700"></div>
                  <div className="flex items-center justify-center space-x-4 relative z-10">
                    <Lock className="w-6 h-6" />
                    <span>Proceed to Secure Payment</span>
                    <div className="bg-white/20 px-4 py-1 rounded-full">
                      ${total.toFixed(2)}
                    </div>
                    <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform duration-300" />
                  </div>
                </button>

                <div className="mt-6 flex items-center justify-center space-x-6 text-sm text-gray-600">
                  <div className="flex items-center space-x-2">
                    <Shield className="w-4 h-4 text-green-600" />
                    <span>Secure Payment</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Leaf className="w-4 h-4 text-green-600" />
                    <span>Eco-Friendly</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Award className="w-4 h-4 text-green-600" />
                    <span>Quality Guaranteed</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {quote.status === "rejected" && (
          <div className={`transition-all duration-1000 delay-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 overflow-hidden">
              <div className="bg-gradient-to-r from-red-600 to-pink-600 px-8 py-6">
                <h3 className="text-2xl font-bold text-white flex items-center">
                  <XCircle className="mr-3" size={24} />
                  Quote Rejected
                </h3>
                <p className="text-red-100 mt-1">We're sorry, but your quote couldn't be approved</p>
              </div>

              <div className="p-8">
                <div className="bg-red-50 rounded-xl p-6 mb-6 border border-red-200">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="w-6 h-6 text-red-500 mt-1" />
                    <div>
                      <h4 className="font-semibold text-red-900 mb-2">Rejection Reason</h4>
                      <p className="text-red-700">{quote.rejection_reason || "No specific reason provided"}</p>
                    </div>
                  </div>
                </div>

                <button className="group relative w-full bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-xl">
                  <div className="flex items-center justify-center space-x-3">
                    <Mail className="w-5 h-5" />
                    <span>Contact Support</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                  </div>
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Custom Styles */}
      <style jsx>{`
        .animation-delay-2000 {
          animation-delay: 2s;
        }

        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default ClientDashboard;